// THE ROUTE CONTRACT (FROZEN — Phase 0).
// Locale-prefixed routes /:lang(zh|en)/... for every page, plus:
//   * `/`                 -> redirect to the visitor's preferred locale: the `lang`
//                            cookie LanguageSwitcher writes if present & valid,
//                            otherwise <DEFAULT_LOCALE> (zh).
//   * `/:lang` invalid    -> redirect to /<DEFAULT_LOCALE>
//   * unknown sub-path    -> redirect to the locale home
// The active lang param drives i18n + <html lang>. Pages are filled in Phase 1 but
// MUST NOT change the route table or param handling.
//
// NOTE: this is the CLIENT-side default-locale redirect (runs after the SPA boots).
// The BFF also performs a server-side "/" redirect that honors the same `lang`
// cookie (plan §6) so first paint lands on the right locale without a client hop;
// this mirror keeps client-only navigations (and dev) consistent.
import { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import Layout from "./components/Layout";
import Home from "./routes/Home";
import About from "./routes/About";
import Services from "./routes/Services";
import Network from "./routes/Network";
import Partners from "./routes/Partners";
import Contact from "./routes/Contact";
import Admin from "./routes/Admin";
import { SUPPORTED_LOCALES, type AppLocale, DEFAULT_LOCALE } from "./i18n";

function isLocale(v: string | undefined): v is AppLocale {
  return !!v && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

/** Read the `lang` cookie LanguageSwitcher writes; undefined if absent/invalid. */
function readLangCookie(): AppLocale | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(/(?:^|;\s*)lang=([^;]*)/);
  const value = match ? decodeURIComponent(match[1]) : undefined;
  return isLocale(value) ? value : undefined;
}

/** Redirect `/` to the visitor's preferred locale (cookie, else DEFAULT_LOCALE). */
function RootRedirect() {
  const preferred = readLangCookie() ?? DEFAULT_LOCALE;
  return <Navigate to={`/${preferred}`} replace />;
}

/** Guards the :lang segment, syncs i18n + document.lang, renders the shell. */
function LocaleLayout() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isLocale(lang) && i18n.language !== lang) {
      void i18n.changeLanguage(lang);
    }
    if (isLocale(lang)) {
      document.documentElement.lang = lang === "zh" ? "zh-Hans" : "en";
    }
  }, [lang, i18n]);

  if (!isLocale(lang)) {
    return <Navigate to={`/${DEFAULT_LOCALE}`} replace />;
  }
  return <Layout />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/:lang" element={<LocaleLayout />}>
        <Route index element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="services" element={<Services />} />
        <Route path="network" element={<Network />} />
        <Route path="partners" element={<Partners />} />
        <Route path="contact" element={<Contact />} />
        {/* Unknown sub-path under a valid locale -> that locale's home. */}
        <Route path="*" element={<Navigate to="" replace />} />
      </Route>
      {/* Operator console — top-level, OUTSIDE the bilingual /:lang group (no i18n). */}
      <Route path="/admin" element={<Admin />} />
      {/* Anything else -> default locale home. */}
      <Route path="*" element={<Navigate to={`/${DEFAULT_LOCALE}`} replace />} />
    </Routes>
  );
}
